/**
 * Shared selection-rationale popover.
 *
 * Used on any param-card field whose `selected.selection_rationale` is
 * non-null. Renders as a small ⓘ icon button next to the field; click
 * opens a Popover with the rationale text. Same component used for
 * Output (in OutputAndSegmentsBlock) and Input (in
 * EditScenariosInputSelectionBody) so the two fields render
 * identically.
 */
import React, { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Popover, Typography } from '@mui/material';

interface SelectionRationalePopoverProps {
  rationale: string;
  /** Accessible label, e.g. "Why this Output was selected". */
  ariaLabel: string;
}

export const SelectionRationalePopover: React.FC<
  SelectionRationalePopoverProps
> = ({ rationale, ariaLabel }) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label={ariaLabel}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ p: 0.25, color: 'text.secondary' }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 360,
              p: 1.5,
            },
          },
        }}
      >
        <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
          {rationale}
        </Typography>
      </Popover>
    </>
  );
};
